import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Icon from 'components/_UI/Icon';
import Text from 'components/_UI/Text';
import styled, { createGlobalStyle } from 'styled-components'
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { Column } from 'styled/alignment/Column';
import userApi from 'api/requests/user';
import Loader from 'components/_UI/Loader';
import { useUser } from 'context/user/User';
import paths from 'routes/paths';
import indexPriority from 'theme/z-index';
import { useAuth } from 'context/auth/Auth';
import Button from 'components/_UI/Button';
import { LogIn, LogOut, User, Shield, UserCheck, Lock, Tag, Plus, ChevronDown, Gift, LayoutDashboard } from 'lucide-react';
import { useOnSelectMenu } from 'context/general/OnSelectMenu';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import NotificationBell from 'components/commons/NotificationBell';
import ROLES from 'helpers/roles';
import LoginModal from 'modules/onboarding/sign-in/LoginModal';
import usePanelPermissions from 'hooks/usePanelPermissions';
import { PERMISSIONS } from 'helpers/panelPermissions';
import useProducerPanelEntry from 'modules/dashboard/producer-access/useProducerPanelEntry';
import PanelSwitcher from 'modules/dashboard/producer-access/PanelSwitcher';
import { leaveToOwnContext, openEventsPanel } from 'modules/dashboard/producer-access/enterProducerPanel';
import { actorHasOwnPanel } from 'helpers/dashboardAccess';

const PRODUCER_PANEL_THEME_CLASS = 'producer-panel-theme';
const ESTABLISHMENT_PANEL_THEME_CLASS = 'establishment-panel-theme';

// Altura única dos controles da direita do header (presente, sino, avatar e
// botões de painel): alinhados, com o mesmo espaço entre eles.
const HEADER_CONTROL_SIZE = 40;
const HEADER_CONTROL_GAP = 12;
const HEADER_CONTROL_BORDER = 'rgba(112, 112, 112, 0.15)';
const HEADER_CONTROL_HOVER = 'rgba(112, 112, 112, 0.06)';

export default function DashHeader({
    visible,
    userView = false,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user, setUser } = useUser();
    const { isAuth } = useAuth();
    const { isActing, can } = usePanelPermissions();
    const { hasAccess: hasProducerPanelAccess, openPanel: openProducerPanel, picker: producerPanelPicker } = useProducerPanelEntry();

    const isEstablishmentRole = user?.role?.id == ROLES.ESTABLISHMENT || user?.role?.id == ROLES.ADMIN;
    // Quem já tem painel próprio (produtor, estabelecimento, admin) e ainda é
    // de equipe de outro produtor: o painel da equipe não ganha um segundo
    // botão "Painel do produtor", entra pelo menu e pelo seletor do painel.
    const hasOwnPanel = actorHasOwnPanel(user);
    const inProducerPanel = location.pathname.startsWith(paths.dashboard.producer.root);
    const inEstablishmentPanel = location.pathname.startsWith(paths.dashboard.establishment.root);

    const panelAccent = inEstablishmentPanel
        ? colors.establishmentAccent
        : inProducerPanel
            ? colors.legacyOccasionalPurple
            : null;
    const panelSuffix = inEstablishmentPanel ? 'estabelecimentos' : inProducerPanel ? 'produtores' : null;
    const showPanelSwitcher = visible && inProducerPanel && !userView;

    // DashSlot é compartilhado pelos painéis de produtor e estabelecimento,
    // então a cor de destaque só é trocada via classe no body enquanto o
    // header estiver montado num desses painéis (mesmo padrão do Home.js).
    useEffect(() => {
        if (!panelAccent) return;

        const panelClass = inEstablishmentPanel ? ESTABLISHMENT_PANEL_THEME_CLASS : PRODUCER_PANEL_THEME_CLASS;
        document.body.classList.add(panelClass);
        return () => document.body.classList.remove(panelClass);
    }, [panelAccent, inEstablishmentPanel]);

    const dropdownItems = [
        {
            name: 'event-panel',
            label: 'Painel de eventos',
            icon: <LayoutDashboard size='16px' color={colors.primaryDashboard} />,
            // Convidado para eventos, sem painel próprio nem de equipe: é o painel dele.
            visible: user?.is_event_collaborator && !visible && !hasProducerPanelAccess && !hasOwnPanel,
            onClick: openEventsPanel,
        },
        {
            name: 'producer-panel',
            label: hasOwnPanel ? 'Painéis de equipe' : 'Painel do produtor',
            icon: <LayoutDashboard size='16px' color={colors.primaryDashboard} />,
            visible: hasProducerPanelAccess && !visible,
            onClick: openProducerPanel,
        },
        {
            name: 'leave-producer-panel',
            label: hasOwnPanel ? 'Voltar ao meu painel' : 'Sair do painel do produtor',
            icon: <LogOut size='16px' color={colors.primaryDashboard} />,
            visible: isActing && visible,
            onClick: () => leaveToOwnContext(user),
        },
        {
            name: 'profile',
            label: user?.role?.id == ROLES.PRODUCER ? 'Resumo' : 'Perfil',
            icon: <User size='16px' color={colors.primaryDashboard} /> ,
            visible: (user?.role?.id == ROLES.USER || visible) && !isEstablishmentRole,
        },
        {
            name: 'switch-to-producer-panel',
            label: 'Painel de Eventos',
            icon: <User size='16px' color={colors.primaryDashboard} />,
            visible: isEstablishmentRole && !inProducerPanel,
        },
        {
            name: 'switch-to-establishment-panel',
            label: 'Painel do Estabelecimento',
            icon: <User size='16px' color={colors.primaryDashboard} />,
            visible: isEstablishmentRole && !inEstablishmentPanel,
        },
        {
            name: 'change-password',
            label: 'Alterar senha',
            icon: <Lock size='16px' color={colors.primaryDashboard} />,
            visible: !isActing,
        },
        // { name: 'delete-account', label: 'Excluir conta', icon: <Trash2 size='16px' color={colors.primaryDashboard} /> },
        {
            name: 'use-terms',
            label: 'Termos de uso',
            icon: <UserCheck size='16px' color={colors.primaryDashboard} />,
            visible: true,
        },
        {
            name: 'privacy-policies',
            label: 'Política de privacidade',
            icon: <Shield size='16px' color={colors.primaryDashboard} />,
            visible: true,
        },
        {
            name: 'logout',
            label: 'Sair',
            icon: <LogOut size='16px' color={colors.primaryDashboard} />,
            visible: true,
        },
    ]

    const [firstLineToggle, setFirstLineToggle] = useState(false);
    const [secondLineToggle, setSecondLineToggle] = useState(false);
    const [visibleDropdown, setVisibleDropdown] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const { setOnSelectMenu, isLateralMenuActive, setIsLateralMenuActive } = useOnSelectMenu()
    const [imageProfile, setImageProfile] = useState('')
    const [nameProfile, setNameProfile] = useState('')

    const authorizedRoles = [
        +ROLES.PRODUCER,
        +ROLES.ESTABLISHMENT,
        +ROLES.ADMIN
    ];

    useEffect(async () => {
        let currentUser = user

        if ((!currentUser || !currentUser?.id) && isAuth) {
            currentUser = await getUser()
        }

        handleProfileImage(currentUser)
    }, [])

    useEffect(async () => {
        handleProfileImage(user)
    }, [user])

    async function handleProfileImage(updatedUser) {
        // Dentro do painel de um produtor, quem aparece é quem de fato entrou.
        const actingMember = updatedUser?.acting_as?.member
        let image = actingMember ? actingMember.avatar : updatedUser?.avatar
        let name = actingMember ? actingMember.first_name : updatedUser?.first_name

        if (updatedUser?.role?.id == ROLES.ESTABLISHMENT) {
            const currentEstablishment = updatedUser?.establishments[0]

            image = currentEstablishment?.profile_image_id?.thumb || image
            name = currentEstablishment?.name || name
        }

        setImageProfile(image)
        setNameProfile(name)
    }

    async function getUser() {
        setIsLoading(true);

        try {
            const { data } = await userApi.me();

            setUser(data);

            return data;
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    function toggleMenu() {
        setFirstLineToggle(!firstLineToggle)
        setSecondLineToggle(!secondLineToggle)
        setIsLateralMenuActive(!isLateralMenuActive)
    }

    function toogleProfile() {
        setVisibleDropdown(!visibleDropdown)
    }

    useEffect(() => {
        if (!visibleDropdown) return;

        function handleClickOutside() {
            setVisibleDropdown(false);
        }

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [visibleDropdown]);

    function goToLogin() {
        navigate({
            pathname: paths.login,
            search: new URLSearchParams({
                ...Object.fromEntries(searchParams),
                redirectTo: location.pathname
            }).toString()
        })
    }

    return (
        <>
        <PanelAccentStyle />
        <StyledHeader onClick={() => setVisibleDropdown(false)}>
            <Brand>
                <WhizrLogo
                    href={paths.home}
                >
                    <LogoMark>
                        <Icon name={'whizr-inline-logo-dark'} />
                    </LogoMark>
                    {panelSuffix && (
                        <LogoSuffix accentColor={panelAccent} hideOnMobile={showPanelSwitcher}>{panelSuffix}</LogoSuffix>
                    )}
                </WhizrLogo>

                {showPanelSwitcher && <PanelSwitcher />}
            </Brand>

            <RightNavContainer>
                {
                    !isAuth ? (
                        <UnauthRow>
                            <HeaderLink onClick={() => navigate(paths.partner.createAccount)}>
                                <Plus size={15} />
                                <span>Crie seu evento</span>
                            </HeaderLink>
                            <LoginButtonContainer>
                                <Button
                                    marginBottom='0'
                                    category='roundedBorded'
                                    onClick={() => setIsLoginModalOpen(true)}
                                >
                                    <LogIn color={colors.neutral} size={18} />
                                    <Text color={colors.neutral} marginLeft={'6px'} paddingBottom={0}>
                                        Entrar ou criar conta
                                    </Text>
                                </Button>
                            </LoginButtonContainer>
                        </UnauthRow>
                    ) : (
                        <>
                            {
                                !userView && (
                                    <HamburguerMenu onClick={toggleMenu}>
                                        <HamburguerMenuLine firstLineToggle={firstLineToggle} />
                                        <HamburguerMenuLine secondLineToggle={secondLineToggle} />
                                    </HamburguerMenu>
                                )
                            }

                            <ContainerUser>
                                {
                                    !user && isLoading ? <Loader /> : (
                                        <>
                                            <HeaderActionsRow>
                                                {
                                                    !visible && (
                                                        <HeaderLink onClick={() => navigate(authorizedRoles?.includes(user?.role?.id) ? paths.dashboard.events : paths.partner.createAccount)}>
                                                            <Plus size={15} />
                                                            <span>Crie seu evento</span>
                                                        </HeaderLink>
                                                    )
                                                }
                                                {
                                                    ((user?.role?.id != ROLES.ESTABLISHMENT) && (user?.role?.id != ROLES.PRODUCER) && !visible) && (
                                                        <HeaderLink onClick={() => navigate(paths.user.wallet)}>
                                                            <Tag size={15} />
                                                            <span>Meus ingressos</span>
                                                        </HeaderLink>
                                                    )
                                                }
                                                {
                                                    (hasProducerPanelAccess && !hasOwnPanel && !visible && !userView) && (
                                                        <ProducerButton onClick={openProducerPanel}>
                                                            <ContentIcon>
                                                                <Icon name='pencil-square' color='#717680' />
                                                            </ContentIcon>
                                                            <span>Painel do produtor</span>
                                                        </ProducerButton>
                                                    )
                                                }
                                                {
                                                    (user?.role?.id == ROLES.PRODUCER && !visible && !userView) && (
                                                        <ProducerButton onClick={() => navigate(paths.dashboard.statistics)}>
                                                            <ContentIcon>
                                                                <Icon name='pencil-square' color='#717680' />
                                                            </ContentIcon>
                                                            <span>Painel do produtor</span>
                                                        </ProducerButton>
                                                    )
                                                }
                                                {
                                                    (isEstablishmentRole && !userView && !inProducerPanel) && (
                                                        <ProducerButton onClick={() => navigate(paths.dashboard.statistics)}>
                                                            <ContentIcon>
                                                                <Icon name='pencil-square' color='#717680' />
                                                            </ContentIcon>
                                                            <span>Painel de Eventos</span>
                                                        </ProducerButton>
                                                    )
                                                }
                                                {
                                                    (isEstablishmentRole && !userView && !inEstablishmentPanel) && (
                                                        <ProducerButton onClick={() => navigate(paths.dashboard.establishment.profile)}>
                                                            <ContentIcon>
                                                                <Icon name='pencil-square' color='#717680' />
                                                            </ContentIcon>
                                                            <span>Painel do Estabelecimento</span>
                                                        </ProducerButton>
                                                    )
                                                }
                                            </HeaderActionsRow>

                                            {
                                                ((inProducerPanel || inEstablishmentPanel) && !userView && can(PERMISSIONS.AFFILIATE_VIEW)) && (
                                                    <HeaderIconButton
                                                        type='button'
                                                        title='Programa de indicação'
                                                        aria-label='Programa de indicação'
                                                        onClick={() => navigate(inEstablishmentPanel ? paths.dashboard.establishment.affiliate : paths.dashboard.affiliate)}
                                                    >
                                                        <Gift size={19} aria-hidden='true' />
                                                    </HeaderIconButton>
                                                )
                                            }

                                            <NotificationBell size={HEADER_CONTROL_SIZE} spacing={0} />

                                            <ContentProfile
                                                dropdown={visibleDropdown}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toogleProfile();
                                                }}
                                            >

                                                {
                                                    !isLoading && user ? (
                                                        <StylePreview desktop>
                                                            <DynamicProfileImage
                                                                word={nameProfile}
                                                                img={imageProfile}
                                                                size={`${HEADER_CONTROL_SIZE - 8}px`}
                                                                variant='rounded'
                                                            />
                                                            <ProfileChevron open={visibleDropdown}>
                                                                <ChevronDown size={16} color={colors.night} />
                                                            </ProfileChevron>
                                                        </StylePreview>

                                                    ) : (
                                                        <Loader />
                                                    )
                                                }


                                            </ContentProfile>

                                            <StylePreview
                                                mobile
                                                dropdown={visibleDropdown}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toogleProfile();
                                                }}>
                                                <DynamicProfileImage
                                                    word={nameProfile}
                                                    img={imageProfile}
                                                    size={'40px'}
                                                    variant='rounded'
                                                />
                                            </StylePreview>

                                            <Dropdown dropdown={visibleDropdown}>
                                                {dropdownItems?.map((el, index) => {
                                                    if (el.visible) {
                                                        return (
                                                            <Items key={index} onClick={el.onClick ?? (() => setOnSelectMenu({ tab: el?.name }))}>
                                                                <ContainerItems>
                                                                    {el.icon}
                                                                    <Text paddingBottom={'0px'} ellipsisAt={'95%'} color={colors.primaryDashboard}>
                                                                        {el.label}
                                                                    </Text>
                                                                </ContainerItems>
                                                            </Items>
                                                        )
                                                    }
                                                })}
                                            </Dropdown>

                                        </>
                                    )
                                }
                            </ContainerUser>
                        </>
                    )
                }

            </RightNavContainer>

        </StyledHeader >

        <LoginModal
            isOpen={isLoginModalOpen}
            onClose={() => setIsLoginModalOpen(false)}
        />
        {producerPanelPicker}
        </>
    )
}


const StyledHeader = styled.header`
    display: flex;
    flex-direction: row;
    padding: 20px;
    justify-content: space-between;
    align-items: center;
    background: ${colors.neutralBackground};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 32;
    box-shadow: 0 1px 8px ${colors.night}08;

    @media screen and (min-width: ${breakpoints.lg}) {
        padding: 20px 40px;
        width: 100%;
        border-bottom: 1px solid ${colors.night}11;
        z-index: 32;
    }
`;

const PanelAccentStyle = createGlobalStyle`
    body.${PRODUCER_PANEL_THEME_CLASS} {
        --panel-accent: ${colors.legacyOccasionalPurple};
        --panel-accent-0a: ${colors.legacyOccasionalPurple}0A;
        --panel-accent-0f: ${colors.legacyOccasionalPurple}0F;
        --panel-accent-10: ${colors.legacyOccasionalPurple}10;
        --panel-accent-15: ${colors.legacyOccasionalPurple}15;
    }

    body.${ESTABLISHMENT_PANEL_THEME_CLASS} {
        --panel-accent: ${colors.establishmentAccent};
        --panel-accent-0a: ${colors.establishmentAccent}0A;
        --panel-accent-0f: ${colors.establishmentAccent}0F;
        --panel-accent-10: ${colors.establishmentAccent}10;
        --panel-accent-15: ${colors.establishmentAccent}15;
    }
`;

const Brand = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    @media screen and (min-width: ${breakpoints.lg}) {
        gap: 20px;
    }
`;

const WhizrLogo = styled.a`
    display: flex;
    align-items: flex-end;
    width: auto;
    cursor: pointer;
    transition: all ease-in-out .25s;

    &:hover {
        transform: scale(1.03)
    }
`;

const LogoMark = styled.div`
    width: 100px;
    height: 30px;
    flex-shrink: 0;

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 120px;
        height: 40px;
    }
`;

const LogoSuffix = styled.span`
    margin-left: 2px;
    margin-bottom: 2px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    color: ${({ accentColor }) => accentColor};

    /* O seletor de painel precisa do espaço no celular. */
    ${({ hideOnMobile }) => hideOnMobile && `
        @media screen and (max-width: ${breakpoints.sm}) {
            display: none;
        }
    `}

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 13px;
        margin-bottom: 4px;
    }
`;

const HamburguerMenu = styled.div`
    width: 50px;
    height: 50px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;

    @media screen and (min-width: ${breakpoints.lg}){
        display: none;
    }
`;

const HamburguerMenuLine = styled.div`
    display: block;
    width: 30px;
    background: ${colors.secondaryDark};
    height: 2px;
    margin: 5px 5px;
    transition: all 0.4s ease;
    z-index: 1;
    
    ${({ firstLineToggle }) => firstLineToggle && `
        transform: rotate(45deg) translate(8px, 2px);
    `}
    
    ${({ secondLineToggle }) => secondLineToggle && `
        transform: rotate(-45deg) translate(6px, 0px);
    `}

`;

const RightNavContainer = styled(Row)`
    width: auto;
    align-items: center;
    flex-direction: row-reverse;
    @media screen and (min-width: ${breakpoints.lg}) {
        margin-right: 25px;
    }
`;

const ProfileChevron = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform .25s ease;
    ${({ open }) => open && `
        transform: rotate(180deg);
    `}
`;

const ContainerItems = styled(Row)`
    justify-content: flex-start;
    padding: 0;
    align-items: center;
    width: auto;
    & > p  {
        padding-bottom: 0;
        margin-left: 8px;
    }
`;

const ContainerUser = styled.div`
    height: 55px;
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: ${HEADER_CONTROL_GAP}px;
    @media screen and (min-width: ${breakpoints.md}) {
        margin-right: 8px;
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
    }
`;

const ContentProfile = styled.div`
    display: none;
    background: transparent;
    justify-content: center;
    border-radius: 100px;
    cursor: pointer;
    align-items: center;
    position: relative;
    transition: background ease-in-out .2s;
    border: 1.5px solid ${HEADER_CONTROL_BORDER};
    gap: 6px;
    box-sizing: border-box;
    height: ${HEADER_CONTROL_SIZE}px;

    &:hover {
        background: ${HEADER_CONTROL_HOVER};
    }

    ${({ dropdown }) => dropdown === true && `
        background: rgba(112, 112, 112, 0.08);
    `}

    @media screen and (min-width: ${breakpoints.lg}) {
        display: flex;
        width: auto;
        padding: 0 10px 0 3px;
    }
`;

const Dropdown = styled(Column)`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 220px;
    max-height: 350px;
    overflow: auto;
    align-items: stretch;
    border-radius: 10px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(112, 112, 112, 0.12);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    padding: 8px 0;
    opacity: 0;
    transform: translateY(8px);
    pointer-events: none;
    transition: all .2s ease;
    z-index: ${indexPriority.high};

    ${({ dropdown }) => dropdown === true && `
        opacity: 1;
        transform: translateY(0);
        pointer-events: auto;
    `}
`;

const Items = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 10px 12px;
    height: auto;
    width: auto;
    margin: 0 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: background .15s ease;
    &:hover {
        background: rgba(105, 12, 228, 0.05);
        p {
            color: ${colors.occasionalPurple} !important;
        }
        & > div > svg {
            stroke: ${colors.occasionalPurple}
        }

        & > div > div > svg {
            fill: ${colors.occasionalPurple}
        }
    }
`;

const UnauthRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    height: 55px;
`;

const LoginButtonContainer = styled.div`
    width: auto;

    & > button {
        width: auto;
        height: 35px;
        padding: 0 20px;
    }
`;

const HeaderActionsRow = styled.div`
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: flex;
        align-items: center;
        gap: 24px;
        margin-right: 8px;
    }
`;

const HeaderLink = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    color: #717680;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s ease;

    &:hover {
        color: #4a4d54;
    }
`;

const HeaderIconButton = styled.button`
    display: none;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-sizing: border-box;
    width: ${HEADER_CONTROL_SIZE}px;
    height: ${HEADER_CONTROL_SIZE}px;
    padding: 0;
    border-radius: 50%;
    border: 1.5px solid ${HEADER_CONTROL_BORDER};
    background: transparent;
    color: #717680;
    cursor: pointer;
    transition: all 0.2s ease;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: flex;
    }

    &:hover {
        background: ${HEADER_CONTROL_HOVER};
        color: #4a4d54;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const ProducerButton = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    color: #717680;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    box-sizing: border-box;
    height: ${HEADER_CONTROL_SIZE}px;
    padding: 0 16px;
    border: 1.5px solid ${colors.night}22;
    border-radius: ${HEADER_CONTROL_SIZE / 2}px;
    transition: all 0.2s ease;

    &:hover {
        background: ${colors.neutralDark};
        border-color: #BFBFBF;
        color: #4a4d54;
    }
`;

const StylePreview = styled(Row)`
    ${({ mobile }) => mobile && `
        justify-content: flex-end;
        @media screen and (min-width: ${breakpoints.lg}) {
            display: none;
        }
    `}
    ${({ desktop }) => desktop && `
         display: none;
        @media screen and (min-width: ${breakpoints.lg}) {
            display: flex;
            align-items: center;
            width: auto;
            & > p {
                padding-bottom: 0px;
            }
        }
    `}
`;


const ContentIcon = styled.div`
    width: 20px;
    height: 20px;
    margin-right: 6px;

    ${({ wallet }) => wallet && `
        width: 20px;
        height: 20px;
        margin-right: 0px;
    `}

    ${({ qrcode }) => qrcode && `
        &:hover {
            & > svg {
                fill: ${colors.neutralLight}
            }
        }
    `}
`;


